import {
    AutomationExecution,
    ExecutionStatus,
    ExecutionStep
} from 'crqlar-react-common/dist/models/automation/automation';

import {isFanOutNode} from '../config/nodeCategories';

// Shared by the test-run strip (`testRunState`), Run details and the runs list.

const TERMINAL_STATUSES: ExecutionStatus[] = [
    ExecutionStatus.COMPLETED,
    ExecutionStatus.FAILED,
    ExecutionStatus.CANCELLED
];

export const isTerminal = (execution: AutomationExecution): boolean => TERMINAL_STATUSES.includes(execution.status);

type StepResult = Record<string, any>;

/** The API stores a JSON string, a row may carry it parsed; anything unreadable is `{}`. */
export const parseStepResult = (result: unknown): StepResult => {
    if (result && typeof result === 'object') return result as StepResult;
    if (typeof result !== 'string' || !result.trim()) return {};

    try {
        const parsed = JSON.parse(result);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

/** A fan-out child starts with a copy of its parent's steps up to the fan-out node; this drops them. */
export const ownSteps = (execution: AutomationExecution): ExecutionStep[] => {
    const steps = execution.executionSteps ?? [];
    if (!execution.startFromNodeId) return steps;

    const fanOutIndex = steps.map(step => step.nodeId).lastIndexOf(execution.startFromNodeId);
    return fanOutIndex < 0 ? steps : steps.slice(fanOutIndex + 1);
};

/** Child runs this run's own fan-out steps queued. */
export const queuedChildren = (execution: AutomationExecution): number => ownSteps(execution)
    .filter(step => isFanOutNode(step.nodeType))
    .reduce((sum, step) => sum + (Number(parseStepResult(step.result).queued) || 0), 0);

export interface FanOutScope {
    queued: number;
    /** Members the audience or team resolved to. */
    total: number;
    /** A dry run stopped at the first few members, or a test run limited to the chosen ones. */
    limited: boolean;
}

/** Null when the run has no fan-out step yet. */
export const fanOutScope = (execution: AutomationExecution): FanOutScope | null => {
    const step = ownSteps(execution).find(entry => isFanOutNode(entry.nodeType));
    if (!step) return null;

    const result = parseStepResult(step.result);
    return {
        queued: Number(result.queued) || 0,
        total: Number(result.audienceSize ?? result.teamSize) || 0,
        limited: result.limitedForDryRun === true || result.limitedForTestRun === true
    };
};
